/**
 * React-Query hooks for the forced "Getting Started" setup checklist.
 *
 *   - useSetupStatus()    → GET  /api/v1/onboarding/setup
 *   - useCompleteSetup()  → POST /api/v1/onboarding/setup/complete
 *
 * Both sit behind the Flask auth gate (`@login_required`), so the Bearer
 * JWT / session cookie is attached by `apiFetch`. On a successful
 * completion we invalidate the `whoami` query (the carrier of
 * `setup_complete`) so the `SetupGate` re-reads and stops redirecting,
 * and we push the fresh status into the setup-status cache so the page
 * flips to its finished state without a refetch round trip.
 *
 * The schema is `.passthrough()` + lenient on the exact shape of
 * `counts` so a small backend field rename at integration time does not
 * break the page — the component only reads `steps`, `done_count`,
 * `total`, and `complete`.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { WHOAMI_KEY } from '@/features/auth/hooks/use-whoami';
import { apiFetch } from '@/lib/api-client';

/** The four checklist steps, each a boolean "done" flag. */
export const SetupStepsSchema = z.object({
  email: z.boolean(),
  avatar: z.boolean(),
  video: z.boolean(),
  followed: z.boolean(),
});
export type SetupSteps = z.infer<typeof SetupStepsSchema>;

/**
 * The backend returns the four step flags at the TOP LEVEL
 * (`email`/`avatar`/`video`/`followed`) plus a redundant `steps` array of
 * `{ key, done }`, alongside `done_count` / `total` / `complete`. We parse
 * the authoritative top-level booleans and re-expose them to the page as a
 * `steps` object (what the checklist renders from); the `steps` array is
 * ignored. `.passthrough()` keeps any extra fields harmless.
 */
export const SetupStatusSchema = z
  .object({
    email: z.boolean(),
    avatar: z.boolean(),
    video: z.boolean(),
    followed: z.boolean(),
    done_count: z.number(),
    total: z.number(),
    complete: z.boolean(),
  })
  .passthrough()
  .transform((d) => ({
    steps: { email: d.email, avatar: d.avatar, video: d.video, followed: d.followed },
    done_count: d.done_count,
    total: d.total,
    complete: d.complete,
  }));
export type SetupStatus = z.infer<typeof SetupStatusSchema>;

export const SETUP_STATUS_KEY = ['onboarding', 'setup'] as const;

export function useSetupStatus(): UseQueryResult<SetupStatus, Error> {
  return useQuery({
    queryKey: SETUP_STATUS_KEY,
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/api/v1/onboarding/setup');
      return SetupStatusSchema.parse(raw);
    },
    // Steps tick as the user creates assets in other tabs / flows, so
    // keep this fresh rather than serving a stale checklist.
    staleTime: 0,
    retry: false,
  });
}

export function useCompleteSetup() {
  const queryClient = useQueryClient();
  return useMutation<SetupStatus, Error, void>({
    mutationFn: async () => {
      const raw = await apiFetch<unknown>('/api/v1/onboarding/setup/complete', {
        method: 'POST',
      });
      return SetupStatusSchema.parse(raw);
    },
    onSuccess: (status) => {
      // The gate reads `setup_complete` off whoami; refresh it so the
      // follow-on navigation to the dashboard is not bounced back to
      // getting-started. Seed the status cache with the returned (now
      // complete) payload.
      void queryClient.invalidateQueries({ queryKey: WHOAMI_KEY });
      queryClient.setQueryData(SETUP_STATUS_KEY, status);
    },
  });
}
